import moment from 'moment'

import { Dashboard } from '../../models/Dashboard'
import { Order } from '../../models/Order'
import { RecordFactory } from '../../models/RecordFactory'
import { getViewTitleFromView } from '../../utils/dashboard'
import { currencyFormat } from '../../utils/numbers'
import { ComponentData } from '../../utils/typing'
import { PocketComponent, PocketComponentProps, usePSComponent } from '../PocketComponent'
import Paragraph from '../app/design/Paragraph'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../app/design/Table'
import { DashboardSkeleton } from '../common/skeletons/DashboardSkeleton'
import { ClientTableCell } from './ClientTableCell'
import { EmptyDashboardLayout } from './EmptyDashboardLayout'
import { KeyMetric } from './KeyMetric'
import { ServiceListTableCell } from './ServiceListTableCell'

type Props = {
    orders: Order[]
    view: number
    dashboard?: Dashboard
    searchText: string
    isLoading: boolean
}

const getDateTitle = (view: number) => {
    if (view === 2 || view === 6) return 'Completed'
    if (view === 7) return 'Cancelled'
    return 'Created'
}

const getDateString = (order: Order, view: number) => {
    if (view === 2 || view === 6) return moment(order.completedDate).format('MMM D, YYYY')
    if (view === 7) return moment(order.cancelledDate).format('MMM D, YYYY')
    return moment(order.createdDate).format('MMM D, YYYY')
}

const viewWillAppear = async (base: PocketComponent<Props>) => {
    return await Promise.all(
        base.props.orders.map((o) => RecordFactory.getInstance().loadOrder(o.id!))
    )
}

function render({
    base: {
        props: { orders, view, dashboard, searchText, isLoading },
    },
}: PocketComponentProps<ComponentData<typeof viewWillAppear>, Props>) {
    if (isLoading) {
        return <DashboardSkeleton />
    }

    return (
        <div className="flex flex-col gap-6">
            <KeyMetric
                metrics={[
                    {
                        title: `${getViewTitleFromView(dashboard, view)} ${
                            orders.length === 1 ? `order` : `orders`
                        }`,
                        value: orders.length.toString(),
                    },
                ]}
            />
            <Table>
                <TableHeader>
                    <TableRow location="head">
                        <TableHead className="font-normal">Client</TableHead>
                        <TableHead className="font-normal">Order</TableHead>
                        <TableHead className="font-normal">Details</TableHead>
                        <TableHead className="text-right font-normal">Amount</TableHead>
                        <TableHead className="font-normal">Status</TableHead>
                        <TableHead className="font-normal">{getDateTitle(view)}</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {orders.map((o) => (
                        <TableRow key={o.id} className="hover:bg-black/4">
                            <TableCell>
                                <ClientTableCell
                                    user={o.person}
                                    subaccount={o.subaccount}
                                    size="sm"
                                />
                            </TableCell>
                            <TableCell>
                                <Paragraph size="sm">{`#${o.number}`}</Paragraph>
                            </TableCell>
                            <TableCell>
                                <ServiceListTableCell services={o.lines} />
                            </TableCell>
                            <TableCell className="text-right">
                                <Paragraph size="sm">
                                    {currencyFormat(o.askingAmount, true)}
                                </Paragraph>
                            </TableCell>
                            <TableCell>
                                <Paragraph size="sm" className="whitespace-nowrap">
                                    {o.statusString}
                                </Paragraph>
                            </TableCell>
                            <TableCell>
                                <Paragraph size="sm" className="whitespace-nowrap">
                                    {getDateString(o, view)}
                                </Paragraph>
                            </TableCell>
                        </TableRow>
                    ))}
                    {orders.length === 0 && (
                        <TableCell colSpan={10}>
                            <EmptyDashboardLayout dashboard={dashboard} searchtext={searchText} />
                        </TableCell>
                    )}
                </TableBody>
            </Table>
        </div>
    )
}

export const OrdersDashboard = usePSComponent(render, viewWillAppear)
